import React, { useEffect } from 'react'
import { CustomHTMLSelect, CustomInput } from 'src/consumer/components/helperComponents'
import DayjsDatePicker from '../../diagnosis/components/DayjsDatePicker'
import { MOMENT_FORMATS } from 'src/consumer/constants'
import CalendarMonth from '@mui/icons-material/CalendarMonth'
import { Box, Typography } from '@mui/material'

export const TESTS_TYPES_MAP = {
  none: { id: 'none', title: 'None' },
  today: { id: 'today', title: 'Today' },
  days: { id: 'days', title: 'Days' },
  asap: { id: 'asap', title: 'Asap' },
  calendar: { id: 'calendar', title: 'Calendar' },
  weeks: { id: 'weeks', title: 'Weeks' },
  months: { id: 'months', title: 'Months' },
  'next-visit': { id: 'next-visit', title: 'Next Visit' },
}

const FINAL_REQUESTED_TESTS_TYPES = [
  TESTS_TYPES_MAP.none, // { id: undefined, title: 'None' }
  TESTS_TYPES_MAP.today, // { id: 'today', title: 'Today' }
  TESTS_TYPES_MAP.days, // { id: 'days', title: 'Days' }
  TESTS_TYPES_MAP.asap, // { id: 'asap', title: 'Asap' }
  TESTS_TYPES_MAP.calendar, // { id: 'calendar', title: 'Calendar' }
  TESTS_TYPES_MAP.weeks, // { id: 'weeks', title: 'Weeks' }
  TESTS_TYPES_MAP.months, // { id: 'months', title: 'Months' }
  TESTS_TYPES_MAP['next-visit'], // { id: 'next-visit', title: 'Next Visit' }
]

export default function InvestigationDateComponent({
  finalRequestedTestsBy,
  setFinalRequestedTestsBy,
}) {
  // Initialize with 'days' as default if not set
  useEffect(() => {
    if (setFinalRequestedTestsBy && typeof setFinalRequestedTestsBy === 'function') {
      if (!finalRequestedTestsBy || !finalRequestedTestsBy?.valueType) {
        setFinalRequestedTestsBy((prev) => {
          // Only set default if valueType is truly undefined/null
          if (!prev?.valueType) {
            return {
              ...(prev || {}),
              valueType: 'days',
            }
          }
          return prev
        })
      }
    }
  }, [finalRequestedTestsBy, setFinalRequestedTestsBy])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        minWidth: { xs: '200px', sm: '240px' },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: '13px',
          fontWeight: 600,
          color: '#4338ca',
          mb: 0.5,
        }}
      >
        Required By
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          border: '1px solid rgba(67, 56, 202, 0.2)',
          borderRadius: '8px',
          overflow: 'hidden',
          background: 'white',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'rgba(67, 56, 202, 0.3)',
          },
          '&:focus-within': {
            borderColor: '#4338ca',
            boxShadow: '0 0 0 3px rgba(67, 56, 202, 0.1)',
          },
        }}
      >
        {finalRequestedTestsBy?.valueType !== 'calendar' ? (
          <CustomInput
            value={finalRequestedTestsBy?.value || ''}
            componentContainerStyles={{ gap: '0px' }}
            pureInputProps={{
              style: {
                width: '130px',
                border: 'none',
                padding: '10px 12px',
                fontSize: '14px',
              },
              placeholder: 'Enter number',
            }}
            inputAnsMessageContainerStyles={{
              padding: '0',
              background: '#fff',
              borderRadius: '0',
              border: 'none',
              borderRight: '1px solid rgba(67, 56, 202, 0.15)',
            }}
            onChange={(e) => {
              if (setFinalRequestedTestsBy && typeof setFinalRequestedTestsBy === 'function') {
                setFinalRequestedTestsBy((prev) => ({
                  ...(prev || {}),
                  value: e.target.value,
                }))
              }
            }}
          />
        ) : (
          <DayjsDatePicker
            date={finalRequestedTestsBy?.calendarValue}
            format="DD-MMM-YYYY"
            placeholder="Select Date"
            buttonProps={{
              variant: 'text',
              startIcon: <CalendarMonth sx={{ fontSize: 18 }} />,
              sx: {
                background: '#fff',
                textTransform: 'none',
                border: 'none',
                width: '140px',
                fontSize: '14px',
                px: 1.5,
                py: 1,
                '&:hover': {
                  backgroundColor: 'rgba(67, 56, 202, 0.05)',
                },
              },
            }}
            onChange={(val) => {
              if (setFinalRequestedTestsBy && typeof setFinalRequestedTestsBy === 'function') {
                setFinalRequestedTestsBy((prev) => ({
                  ...(prev || {}),
                  calendarValue: val.format(MOMENT_FORMATS.formatThatServerSends),
                }))
              }
            }}
          />
        )}
        <CustomHTMLSelect
          value={finalRequestedTestsBy?.valueType || 'days'}
          outerMostDivStyle={{ minWidth: '100px' }}
          getOptionValue={(optn) => optn?.id}
          getOptionLabel={(optn) => optn?.title}
          style={{
            padding: '10px 12px',
            background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.08) 0%, rgba(99, 102, 241, 0.08) 100%)',
            borderRadius: '0 8px 8px 0',
            border: 'none',
            fontSize: '14px',
            fontWeight: 500,
            color: '#4338ca',
            cursor: 'pointer',
          }}
          options={FINAL_REQUESTED_TESTS_TYPES}
          onChange={(e) => {
            if (!setFinalRequestedTestsBy || typeof setFinalRequestedTestsBy !== 'function') {
              return
            }
            if (e.target.value === TESTS_TYPES_MAP.none.id) {
              setFinalRequestedTestsBy((prev) => ({
                ...(prev || {}),
                valueType: undefined,
                value: undefined,
              }))
              return
            }
            setFinalRequestedTestsBy((prev) => ({
              ...(prev || {}),
              valueType: e.target.value,
              // Clear value when switching types (except calendar which keeps calendarValue)
              ...(e.target.value !== 'calendar' ? { value: prev?.value || '' } : {}),
            }))
          }}
        />
      </Box>
    </Box>
  )
}
